import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'wh_bl';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    wh_bl: true;
  }
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
};

const PRIMARY = {
  lighter: '#fce9e6',
  light: '#ffccb8',
  main: '#FFAB8A',
  dark: '#fe8b5b',
  darker: '#fc7236',
  contrastText: '#000000',
};

const SECONDARY = {
  lighter: '#000000',
  light: '#fff7df',
  main: '#FFEAAE',
  dark: '#fedc7b',
  darker: '#fdcf46',
  contrastText: '#000000',
};

const WH_BL = {
  lighter: '#EEEEEE',
  light: '#fff7df',
  main: '#FFFFFF',
  dark: '#000000',
  darker: '#fdcf46',
  contrastText: '#000000',
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#22C55E',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: '#ffffff',
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#E17678',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  wh_bl: WH_BL,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: '#000000',
      secondary: GREY[600],
      disabled: '#8A8A8A',
      footer: '#FFFFFF',
    },
    background: {
      paper: '#F9F9F9',
      default: '#FFFFFF',
      other: '#EEEEEE',
      footer: '#1E1E1E',
      neutral: GREY[200],
      contrast: '#1E1E1E',
      hover_expanded: '#F2F9FF'
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: '#EEEEEE',
    },
    background: {
      paper: '#242424',
      default: '#1E1E1E',
      neutral: alpha(GREY[500], 0.12),
      contrast: '#FFFFFF',
      hover_expanded: '#C4D8E7'
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  };

  return mode === 'light' ? light : dark;
}
